import { BULK_ORDER_GRID_SIGNS } from './config'
import { DEFAULT_GRID_CONTEXT } from './cartUtils'
import { resolveGridSide } from './sideUtils'

const GRID_CONTEXT_KEYS = ['diameter', 'side', 'cylinderSign', 'additionSign']

const GRID_SIDES = new Set(['R', 'L', 'B'])

const GRID_SIGN_VALUES = new Set(Object.values(BULK_ORDER_GRID_SIGNS))

const getPhysicalDiameters = lensDetail =>
  (lensDetail?.diameters || [])
    .map(diameterItem => diameterItem?.physical)
    .filter(physical => physical != null)

const isValidDiameter = value => value === null || Number.isFinite(value)

const isValidSide = value => GRID_SIDES.has(value)

const isValidSign = value => GRID_SIGN_VALUES.has(value)

/**
 * Apply a partial gridContext patch with key allowlist and value checks.
 * Unknown keys and invalid values are ignored. Stable no-op when unchanged.
 */
export const applyGridContextPatch = (gridContext, patch) => {
  if (patch == null || typeof patch !== 'object' || Array.isArray(patch)) {
    return gridContext
  }

  const next = { ...gridContext }
  let changed = false

  for (const key of GRID_CONTEXT_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(patch, key)) continue

    const value = patch[key]
    let accepted = false

    if (key === 'diameter') {
      accepted = isValidDiameter(value)
    } else if (key === 'side') {
      accepted = isValidSide(value)
    } else if (key === 'cylinderSign' || key === 'additionSign') {
      accepted = isValidSign(value)
    }

    if (!accepted || next[key] === value) continue
    next[key] = value
    changed = true
  }

  return changed ? next : gridContext
}

/**
 * Reconcile grid diameter/side against a newly loaded lens detail.
 * Preserves diameter when still listed; otherwise first diameter or null.
 * Side via resolveGridSide. Signs kept from gridContext (with defaults).
 */
export const resolveGridContextForLensDetail = (gridContext, lensDetail) => {
  const cylinderSign =
    gridContext?.cylinderSign || DEFAULT_GRID_CONTEXT.cylinderSign
  const additionSign =
    gridContext?.additionSign || DEFAULT_GRID_CONTEXT.additionSign

  if (!lensDetail) {
    return {
      ...DEFAULT_GRID_CONTEXT,
      cylinderSign,
      additionSign,
    }
  }

  const physicalDiameters = getPhysicalDiameters(lensDetail)
  const currentDiameter = gridContext?.diameter
  const diameter =
    currentDiameter != null && physicalDiameters.some(d => d === currentDiameter)
      ? currentDiameter
      : physicalDiameters[0] ?? null

  const side = resolveGridSide(lensDetail.hasSideSelection === true, gridContext?.side)

  return {
    ...DEFAULT_GRID_CONTEXT,
    diameter,
    side,
    cylinderSign,
    additionSign,
  }
}
